import '../Banner/Banner.css';
import callApi from 'lib/callApi';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import BannerShell from '../Banner/BannerShell';

interface ReleaseApiResponse {
  enabled: boolean;
  release_number?: string;
  message?: string;
  poll_interval: number;
}

const DEFAULT_POLL_INTERVAL = 300; // Default poll interval in seconds
const CLOSE_ANIMATION_DURATION = 400;
const LOCAL_STORAGE_KEY_PREFIX = 'dismissedRelease_';

const ReleaseBanner: React.FC = () => {
  const [data, setData] = useState<ReleaseApiResponse | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dismissedInSession, setDismissedInSession] = useState(false);
  const lastCloseAtRef = useRef<number>(0);
  // Scrolling/overflow is handled inside BannerShell

  const defaultMessage =
    "A new version of Security Program 360 is available. Click 'Reload' to apply the latest updates.";

  const fetchRelease = useCallback(async () => {
    // While closing, skip making any API calls entirely
    if (isClosing) return;
    // Also skip immediately after close to avoid racing poll call
    const SAFE_WINDOW = CLOSE_ANIMATION_DURATION + 100; // ms
    if (Date.now() - lastCloseAtRef.current < SAFE_WINDOW) return;
    try {
      const res = await callApi('system-settings/release/', 'GET');
      const payload = (res?.data || {}) as ReleaseApiResponse;

      const normalized: ReleaseApiResponse = {
        enabled: !!payload.enabled,
        release_number: payload.release_number || '',
        message: (payload.message && payload.message.trim()) || defaultMessage,
        poll_interval: payload.poll_interval || DEFAULT_POLL_INTERVAL,
      };

      setData(normalized);

      const dismissed = localStorage.getItem(
        `${LOCAL_STORAGE_KEY_PREFIX}${normalized.release_number}`,
      );
      // Avoid showing while closing to prevent blank reserved space
      if (
        normalized.enabled &&
        !dismissed &&
        !dismissedInSession &&
        !isClosing
      ) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } catch {
      // Silent fail
    }
  }, [isClosing, dismissedInSession]);

  useEffect(() => {
    fetchRelease();
  }, [fetchRelease]);

  useEffect(() => {
    // Always poll for updates regardless of banner state
    const pollInterval = data?.poll_interval || DEFAULT_POLL_INTERVAL;
    const interval = setInterval(fetchRelease, pollInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchRelease, data?.poll_interval]);

  // If message changed from previous one, allow showing again in-session
  useEffect(() => {
    if (data?.message) {
      setDismissedInSession(false);
    }
  }, [data?.message]);

  // Rendering and overflow moved into BannerShell

  const handleClose = () => {
    lastCloseAtRef.current = Date.now();
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      // In-memory suppression until refresh
      setDismissedInSession(true);
      // Reset closing state after animation completes so future polls can show banner
      setIsClosing(false);
    }, CLOSE_ANIMATION_DURATION);
  };

  const handleDismissAndReload = () => {
    if (data) {
      localStorage.setItem(
        `${LOCAL_STORAGE_KEY_PREFIX}${data.release_number || ''}`,
        'true',
      );
    }
    window.location.reload();
  };

  if (!data?.enabled || !isVisible) return null;

  return (
    <BannerShell
      visible={!!data?.enabled && isVisible}
      isClosing={isClosing}
      typeClass={'info'}
      iconClass={'tim-icons icon-refresh-02'}
      message={data?.message || ''}
      onOk={handleClose}
      extraActions={
        <button className="banner-button" onClick={handleDismissAndReload}>
          Reload
        </button>
      }
    />
  );
};

export default ReleaseBanner;
